import { PlusCircleIcon } from "@heroicons/react/24/solid";
import type React from "react";
import { type FormEvent, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import CodeInput from "@/atoms-react/input/CodeInput.react";
import InputLabel from "@/atoms-react/input/Input.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Modal, ModalContent } from "@/atoms-react/modal/index.react";
import Tag from "@/atoms-react/tag/Tag.react";
import { Base, CardTitle, SectionTitle } from "@/atoms-react/text/Text.react";
import { getJwt } from "@/features/auth/utils";
import { useCompany } from "@/features/company/hooks/use-company";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { MandateService } from "@/features/company/types";
import { Routes } from "@/features/router";
import useRequestSensitiveEmailMutation from "@/features/security/hooks/use-request-sensitive-email-code.ts";
import useValidateSensitiveEmailMutation from "@/features/security/hooks/use-validate-sensitive-email-code.ts";
import { useCreateEmail } from "@/features/user/email/hooks/use-create-email";
import { useAliasMail } from "@/features/user/hooks/use-alias-mail";
import { useUser } from "@/features/user/hooks/use-user";
import { useUserCheckAccess } from "@/features/user/hooks/use-user-check-access";
import { validateEmailFormat } from "@/features/utils/functions";
import { EmailCard } from "@/molecules/email-card/index.react";
import { ModalDeleteAlias } from "@/molecules/modal-delete-alias/index.react";
import { Themes } from "@/types/theme";
import { AliasMailSchema, CreateAliasMailDto } from "@/types/user/alias-mail.types";
import { extractErrorDescription } from "@/utils/internal-code-handler";
import { LayoutTitle } from "../../../_layout/index.react";

type AliasesProps = { isOnboarding?: boolean } & React.ComponentPropsWithoutRef<"div">;

export const AliasesEmailApp = ({ isOnboarding = false, ...props }: AliasesProps) => {
  const translation = useTranslation(["pass", "error"]);
  const { t } = translation;
  const { useUserProfile } = useUser();
  const { data: user } = useUserProfile();

  const [newEmail, setNewEmail] = useState<string>();
  const [uuidSensitiveActionValidation, setUuidSensitiveActionValidation] = useState<string>();
  const { mutateAsync: sendCode, isPending: isSendingCode } = useRequestSensitiveEmailMutation();
  const { isPending: isPendingValidateEmail } = useValidateSensitiveEmailMutation();

  const { useCheckEmailMutation } = useCreateEmail();
  const { mutateAsync: checkAlias, isPending: isCheckingAlias } = useCheckEmailMutation();

  const { useGetAliasMail, useDeleteAliasMail, useCreateAliasMail } = useAliasMail();
  const { data: aliasMail, isLoading: aliasMailLoading } = useGetAliasMail({
    enabled: !!getJwt()
  });
  const { mutateAsync: deleteAliasMail, isPending: deleteAliasMailIsPending } =
    useDeleteAliasMail();
  const { mutateAsync: createAliasMail } = useCreateAliasMail();

  const [validationMailModal, setValidationMailModal] = useState<boolean>(false);
  const [deletedAliasMail, setDeletedAliasMail] = useState<AliasMailSchema | null>(null);
  const { companyDetailQuery } = useCompany();

  const { leUuid } = useCompanyContext();
  const { data: companyDetails } = companyDetailQuery(leUuid as string);
  const { checkPermission } = useUserCheckAccess();
  const enabled = (() =>
    !leUuid
      ? true
      : !companyDetails?.legalEntity.UserValidation &&
        checkPermission(MandateService.ALIASES_MANAGEMENT, user, companyDetails))();

  const navigate = useNavigate();
  const actionType = "new_alias_mail";

  const onNewEmail = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newEmail) {
      return;
    }
    const validEmail = validateEmailFormat(newEmail);
    if (!validEmail) {
      toast.error(t("flow.id_control.error.email_invalid"));
      return;
    }
    try {
      await checkAlias(newEmail);
      const { data } = await sendCode({ email: newEmail, actionType });
      setUuidSensitiveActionValidation(data?.uuid);
      setValidationMailModal(true);
    } catch (e) {
      if (e instanceof Error) toast.error(e.message);
    }
  };

  const onCreate = async (otpCode: string): Promise<void> => {
    try {
      const dto: CreateAliasMailDto = {
        mail: newEmail ?? "",
        otpValidation: {
          otpCode: otpCode,
          sensitiveActionValidationUuid: uuidSensitiveActionValidation ?? ""
        }
      };

      await createAliasMail(dto);

      toast.success(t("olkypass.accountPage.emails_section.text.email_added"));
      setValidationMailModal(false);
      setNewEmail("");
    } catch (_error: unknown) {
      toast.error(t("olkypass.accountPage.emails_section.text.wrong_otp"));
    }
  };

  const onDelete = async () => {
    if (!deletedAliasMail || !enabled) return;
    try {
      await deleteAliasMail({ uuid: deletedAliasMail.uuid });
    } catch (error: unknown) {
      toast.error(extractErrorDescription(error, translation));
    }
    setDeletedAliasMail(null);
  };

  return (
    <Column {...props}>
      {!isOnboarding && <LayoutTitle>{t("aliasEmailPage.title")}</LayoutTitle>}

      {aliasMailLoading ? (
        <div className="mt-10 flex w-full justify-center">
          <Loader />
        </div>
      ) : (
        <>
          <div className="2xl:w-2/3">
            <div className="mb-8 md:mb-10 md:rounded-sm md:border-2 md:border-slate-50 md:px-6 md:py-4 dark:md:border-2 dark:md:border-slate-800">
              <div
                className="mb-2 flex w-full flex-wrap-reverse items-center justify-center gap-x-3 md:mb-4 md:flex-wrap md:justify-start"
                data-cy="main-alias-mail"
              >
                {aliasMail && aliasMail.length > 0 && (
                  <CardTitle className="truncate font-semibold">{aliasMail[0]?.mail}</CardTitle>
                )}
                <Tag className="mb-2 md:mb-0">
                  {t("olkypass.accountPage.emails_section.badge.main_mail")}
                </Tag>
              </div>
              <Base>{t("olkypass.accountPage.emails_section.text.main_mail_text")}</Base>
            </div>

            <SectionTitle className="mb-4 md:mb-6">
              {t("olkypass.accountPage.emails_section.section.other_mails_section")}
            </SectionTitle>
            <div>
              <form onSubmit={onNewEmail} className="mb-4 gap-x-3 md:flex">
                <InputLabel
                  type="email"
                  placeholder="email@gmail.com"
                  value={newEmail}
                  disabled={!enabled}
                  onChange={(value: string) => setNewEmail(value)}
                  className="mb-1 flex-1 md:mb-0"
                  data-cy="input-add-email"
                />
                <Button
                  onClick={() => {}}
                  type="submit"
                  disabled={!newEmail || !enabled}
                  theme={Themes.white}
                  icon={<PlusCircleIcon />}
                  loading={isSendingCode || isCheckingAlias}
                  className="md:w-fit"
                  data-cy="button-add-email"
                >
                  {t("olkypass.general.add")}
                </Button>
              </form>
            </div>
            <ModalDeleteAlias
              isOpen={!!deletedAliasMail}
              onClose={() => setDeletedAliasMail(null)}
              onValidated={onDelete}
              disabled={!enabled}
              isConfirmLoading={deleteAliasMailIsPending}
            >
              <Base noColor={true}>
                {t("aliasEmailPage.confirm_delete", { name: deletedAliasMail?.mail })}
              </Base>
            </ModalDeleteAlias>
            <div className="h-full w-full">
              {aliasMail &&
                aliasMail.slice(1).map((email, index) => (
                  <EmailCard
                    key={index}
                    email={email}
                    onPublicClick={() => navigate(Routes.account_alias_public)}
                    disabled={!enabled}
                    dataCy={"email-list-item"}
                    onDelete={() => {
                      setDeletedAliasMail(email);
                    }}
                  />
                ))}
            </div>
          </div>
        </>
      )}

      <Modal
        maxWidthClassName="max-w-2xl"
        open={validationMailModal}
        onClose={() => setValidationMailModal(false)}
      >
        <ModalContent title={t("flow.id_control.signup.label.verify_email_title")}>
          <div className="mt-4 flex flex-col items-center gap-4">
            <div>
              {t("flow.id_control.signup.label.verify_email_description_1")}{" "}
              <Base className={"inline font-semibold"}>{newEmail ?? ""}</Base>
              {"."}
            </div>
            <CodeInput
              codeNumberOfCharacters={6}
              className={"mb-6"}
              focus={true}
              onChange={(code) => onCreate(code)}
              loading={isPendingValidateEmail}
            />
          </div>
        </ModalContent>
      </Modal>
    </Column>
  );
};
